import { useState, useEffect, Fragment } from 'react'
import { Form as FinalForm, Field } from 'react-final-form'
import { useTranslation } from 'react-i18next'
import { styled, Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { Grid, Typography, Tooltip, Button } from '@mui/material'
import Switch from '../../control/Switch'
import Dialog from '../../control/Dialog'
import Textbox from '../../control/Textbox'
import DropdownInput from '../input/DropdownInput'
import useUser, { Form, validate } from '../../hooks/backoffice/useUser'
import BackIcon from '@mui/icons-material/ArrowBackIosNewRounded'
import { ReactComponent as ShowIcon } from '../../icon/login_password_visible.svg'
import { ReactComponent as HideIcon } from '../../icon/login_password_invisible.svg'
import { useSelector } from 'react-redux'
import { RootStateType } from '../../redux/reducer/rootReducer'

type ManageUserFormProps = {
    type: 'add' | 'edit'
}

const ManageUserForm = (props: ManageUserFormProps) => {
    const { type } = props
    const {
        enableUser,
        userGroupList,
        departmentList,
        alertMessageError,
        openDialogError,
        alertMessageOk,
        openDialogBack,
        openDialogSave,
        openDialogOk,
        onClickConfirmSave,
        onClickCancelSave,
        onClickBack,
        onClickForceBack,
        onClickCancelBack,
        onClickConfirmBack,
        onClickOk,
        onSubmitForm,
        onChangeEnableUser,
        callServiceGetById,
        setEnableUser,
    } = useUser()
    const { t } = useTranslation()
    const classes = useStyles()

    const validates = (values: Form) => {
        const validation = validate(values, t, type) || {}
        return validation
    }

    const [showISYL, setShowISYL] = useState(false)
    const [showConfirmISYL, setShowConfirmISYL] = useState(false)
    const [userId, setUserId] = useState<number>(0)
    const [titleName, setTitleName] = useState<string>()
    const [initialValues, setInitialValues] = useState<Form>({
        username: '',
        email: '',
        isyl: '',
        confirmIsyl: '',
        firstname: '',
        lastname: '',
        userEnabled: true,
    })

    const { isLoggedIn } = useSelector(({ profile }: RootStateType) => profile)

    useEffect(() => {
        if (type === 'add') {
            setTitleName(`${t('BUTTON.ADD')}${t('LABEL.BACKOFFICE.USER.TITLE')}`)
        }
        if (type === 'edit') {
            setTitleName(`${t('BUTTON.EDIT')}${t('LABEL.BACKOFFICE.USER.TITLE')}`)
        }
    }, [])

    useEffect(() => {
        if (type === 'edit' && isLoggedIn) {
            const url = globalThis.location.href
            const splitUrl = url.split('/')
            const id = parseInt(splitUrl[splitUrl.length - 2])
            if (id) {
                setUserId(id)
                callServiceGetInitial(id)
            }
        }
    }, [departmentList, isLoggedIn])

    const callServiceGetInitial = async (id: number) => {
        const { result } = await callServiceGetById(id)

        if (result?.data) {
            setInitialValues({
                username: result?.data.username,
                email: result?.data.email,
                firstname: result?.data.firstName,
                lastname: result?.data.lastName,
                userGroupId: result?.data.groupId,
                departmentId: result?.data.departmentId,
                userEnabled: result?.data.isEnabled,
            })
            setEnableUser(result?.data.isEnabled as boolean)
        }
    }

    return (
        <Fragment>
            <FinalForm onSubmit={onSubmitForm} validate={validates} initialValues={initialValues}>
                {({ form, handleSubmit, values, dirty }) => (
                    <form onSubmit={handleSubmit}>
                        <StyledDivHeader>
                            <Tooltip title={`กลับไปหน้า${t('MENU.USER_MANAGEMENT')}`} placement="top">
                                <Button
                                    onClick={() => {
                                        onClickBack(dirty)
                                    }}
                                    className={classes.backButton}
                                >
                                    <IconBack />
                                </Button>
                            </Tooltip>
                            <StyledTypoHeader variant="h6">{titleName}</StyledTypoHeader>
                        </StyledDivHeader>
                        <StyledDivForm>
                            <Grid container spacing={2} className={classes.container}>
                                {type === 'add' && (
                                    <Grid item xs={12} md={6}>
                                        <Field
                                            name="username"
                                            label={`${t('LABEL.BACKOFFICE.USER_GROUP.NAME')}${t(
                                                'LABEL.BACKOFFICE.USER.TITLE',
                                            )}`}
                                            placeholder={`${t('LABEL.BACKOFFICE.USER.NAME')}${t(
                                                'LABEL.BACKOFFICE.USER.TITLE',
                                            )}`}
                                            component={Textbox}
                                            fullWidth
                                            required
                                            inputProps={{ autoComplete: 'new-username' }}
                                        />
                                    </Grid>
                                )}
                                <Grid item xs={12} md={type === 'add' ? 6 : 12}>
                                    <Field
                                        name="email"
                                        label={t('LABEL.BACKOFFICE.USER.EMAIL')}
                                        placeholder={t('LABEL.BACKOFFICE.USER.EMAIL')}
                                        component={Textbox}
                                        required
                                        fullWidth
                                    />
                                </Grid>
                                {type === 'add' && (
                                    <Fragment>
                                        <Grid item xs={12} md={6}>
                                            <Field
                                                name="isyl"
                                                type={showISYL ? 'text' : 'password'}
                                                label={t('LABEL.BACKOFFICE.USER.PASSWORD')}
                                                placeholder={t('LABEL.BACKOFFICE.USER.PASSWORD')}
                                                component={Textbox}
                                                fullWidth
                                                required
                                                endAdornment={
                                                    <Fragment>
                                                        {!showISYL && (
                                                            <IconShow
                                                                style={{
                                                                    width: '20px',
                                                                    height: '20px',
                                                                    cursor: 'pointer',
                                                                }}
                                                                onClick={() => setShowISYL(true)}
                                                            />
                                                        )}
                                                        {showISYL && (
                                                            <IconHide
                                                                style={{
                                                                    width: '20px',
                                                                    height: '20px',
                                                                    cursor: 'pointer',
                                                                }}
                                                                onClick={() => setShowISYL(false)}
                                                            />
                                                        )}
                                                    </Fragment>
                                                }
                                                inputProps={{ autoComplete: 'new-password' }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Field
                                                name="confirmIsyl"
                                                type={showConfirmISYL ? 'text' : 'password'}
                                                label={t('LABEL.BACKOFFICE.USER.CONFIRM_PASSWORD')}
                                                placeholder={t('LABEL.BACKOFFICE.USER.CONFIRM_PASSWORD')}
                                                component={Textbox}
                                                fullWidth
                                                required
                                                endAdornment={
                                                    <Fragment>
                                                        {!showConfirmISYL && (
                                                            <IconShow
                                                                style={{
                                                                    width: '20px',
                                                                    height: '20px',
                                                                    cursor: 'pointer',
                                                                }}
                                                                onClick={() => setShowConfirmISYL(true)}
                                                            />
                                                        )}
                                                        {showConfirmISYL && (
                                                            <IconHide
                                                                style={{
                                                                    width: '20px',
                                                                    height: '20px',
                                                                    cursor: 'pointer',
                                                                }}
                                                                onClick={() => setShowConfirmISYL(false)}
                                                            />
                                                        )}
                                                    </Fragment>
                                                }
                                            />
                                        </Grid>
                                    </Fragment>
                                )}
                                <Grid item xs={12} md={6}>
                                    <Field
                                        name="firstname"
                                        label={t('LABEL.BACKOFFICE.USER.NAME')}
                                        placeholder={t('LABEL.BACKOFFICE.USER.NAME')}
                                        component={Textbox}
                                        fullWidth
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Field
                                        name="lastname"
                                        label={t('LABEL.BACKOFFICE.USER.LAST_NAME')}
                                        placeholder={t('LABEL.BACKOFFICE.USER.LAST_NAME')}
                                        component={Textbox}
                                        fullWidth
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Field
                                        name="userGroupId"
                                        label={t('LABEL.BACKOFFICE.USER_GROUP.TITLE')}
                                        placeholder={`${t('LABEL.SELECT_REQUIRED')}${t(
                                            'LABEL.BACKOFFICE.USER_GROUP.TITLE',
                                        )}`}
                                        component={DropdownInput}
                                        options={userGroupList
                                            ?.filter((item) => item.id !== 0)
                                            .map((item) => ({
                                                value: item.id,
                                                label: item.name,
                                            }))}
                                        onChange={(evt: any) => {
                                            form.change('userGroupId', evt)
                                        }}
                                        fullWidth
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Field
                                        name="departmentId"
                                        label={t('LABEL.BACKOFFICE.USER.ORGANIZATION')}
                                        placeholder={`${t('LABEL.SELECT_REQUIRED')}${t(
                                            'LABEL.BACKOFFICE.USER.ORGANIZATION',
                                        )}`}
                                        component={DropdownInput}
                                        options={departmentList?.map((item) => ({
                                            value: item.id,
                                            label: item.name,
                                        }))}
                                        fullWidth
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} className={classes.divStatus}>
                                    <div>
                                        <StyledTypoStatus>สถานะการใช้งาน</StyledTypoStatus>
                                    </div>
                                    <div>
                                        <Switch
                                            labelOn={t('BUTTON.OPEN')}
                                            labelOff={t('BUTTON.CLOSE')}
                                            checked={enableUser}
                                            onChange={onChangeEnableUser}
                                            width={60}
                                            labelLeft={12}
                                            labelRight={15}
                                        />
                                    </div>
                                </Grid>
                                <Grid item xs={12} className={classes.divButtonGroup}>
                                    <div className={classes.divButton}>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => {
                                                onClickBack(dirty)
                                            }}
                                            classes={{ root: classes.button }}
                                        >
                                            {t('BUTTON.CANCEL')}
                                        </Button>
                                    </div>
                                    <div className={classes.divButton}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            type="submit"
                                            classes={{ root: classes.button }}
                                        >
                                            {t('BUTTON.SAVE')}
                                        </Button>
                                    </div>
                                </Grid>
                            </Grid>
                        </StyledDivForm>
                    </form>
                )}
            </FinalForm>
            <Dialog
                type="confirm"
                title={t('ALERT.TITLE.NOTIFY')}
                content={t('ALERT.CONTENT.DISCARD_CHANGE')}
                isOpen={openDialogBack ? true : false}
                onClickCancel={onClickConfirmBack}
                onClickConfirm={onClickCancelBack}
                labelButtonCancel={t('BUTTON.LEAVE')}
                labelButtonConfirm={t('BUTTON.STAY')}
                disableClickBackdrop
                hideCloseButton
            />
            <Dialog
                type="confirm"
                title={t('ALERT.TITLE.NOTIFY')}
                content={'ยืนยันบันทึกข้อมูล'}
                isOpen={openDialogSave}
                onClickConfirm={() => {
                    onClickConfirmSave(type, userId)
                }}
                onClickCancel={onClickCancelSave}
                labelButtonCancel={t('BUTTON.CANCEL')}
                labelButtonConfirm={t('BUTTON.CONFIRM')}
                disableClickBackdrop
                hideCloseButton
            />
            <Dialog
                type="ok"
                title={t('ALERT.TITLE.NOTIFY')}
                content={alertMessageOk}
                isOpen={openDialogOk}
                onClickOk={onClickOk}
                labelButtonOk={t('BUTTON.OK')}
                disableClickBackdrop
                hideCloseButton
            />
            <Dialog
                type="ok"
                severity="error"
                title={t('ALERT.TITLE.ERROR')}
                content={alertMessageError}
                isOpen={openDialogError}
                onClickOk={onClickOk}
                labelButtonOk={t('BUTTON.OK')}
                disableClickBackdrop
                hideCloseButton
            />
        </Fragment>
    )
}

export default ManageUserForm

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    divButtonGroup: {
        display: 'flex',
        justifyContent: 'end',
        paddingBottom: '15px',
    },
    divStatus: {
        display: 'flex',
    },
    divButton: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginLeft: theme.spacing(1),
    },
    button: {
        width: '125px',
        minHeight: '37px',
        fontSize: '14px !important',
    },
    backButton: {
        padding: '0px !important',
        minWidth: '25px !important',
        minHeight: '25px !important',
        marginRight: '10px !important',
        color: `transparent !important`,
    },
    checkbox: {
        color: `${theme.palette.primary.dark} !important`,
        padding: '5px 10px !important',
    },
    checkboxChecked: {
        color: `${theme.palette.primary.dark} !important`,
        padding: '5px 10px !important',
    },
}))

const StyledDivHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    paddingBottom: '15px',
}))

const StyledTypoHeader = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
}))

const StyledDivForm = styled('div')(({ theme }) => ({
    width: '100%',
    padding: '15px 30px',
    marginBottom: '15px',
    borderRadius: '5px',
    backgroundColor: theme.palette.text.secondary,
    boxShadow: theme.palette.getContrastText('shadow-bottom-gray'),
}))

const StyledTypoStatus = styled(Typography)(({ theme }) => ({
    marginRight: '10px',
}))

const IconShow = styled(ShowIcon)(({ theme }) => ({
    width: '14px',
}))

const IconHide = styled(HideIcon)(({ theme }) => ({
    width: '14px',
}))

const IconBack = styled(BackIcon)(({ theme }) => ({
    color: theme.palette.primary.main,
}))
